import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../utils/api";
import Modal from "../components/Modal";

const WithdrawAmount = () => {
  const { userId, fdNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if this is admin or user view
  const isAdminView = location.pathname.includes("/admin/");

  const [fdInfo, setFdInfo] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [errors, setErrors] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userName, setUserName] = useState("");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);

  const [paymentPlatform, setPaymentPlatform] = useState("");
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (
      showCompleteModal ||
      showConfirmModal ||
      showDeleteModal ||
      showContactModal
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showCompleteModal, showConfirmModal, showDeleteModal, showContactModal]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let fdRes;
        let userRes;

        if (isAdminView) {
          fdRes = await api.get(
            `/admin/clients/${userId}/investments/${fdNumber}`
          );
          userRes = await api.get(`/admin/clients/${userId}`);
        } else {
          // User view - use user's own data
          fdRes = await api.get(`/user/investments/${fdNumber}`);
          userRes = await api.get(`/user/profile`);
        }

        setFdInfo(fdRes.data.data);
        setUserName(userRes.data.data.name);
      } catch (err) {
        setErrorMessage("Failed to load investment information");
        console.error("Data fetch error:", err);
      }
    };
    fetchData();
  }, [userId, fdNumber, isAdminView]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      // 5MB limit
      setUploadError("File size exceeds 5MB limit");
      return;
    }
    setPaymentScreenshot(file);
    setUploadError("");
  };

  const handleChange = (e) => {
    setWithdrawAmount(e.target.value);
    setErrors("");
  };

  const validate = () => {
    const amt = parseFloat(withdrawAmount);
    if (isNaN(amt) || amt <= 0) {
      setErrors("Enter a positive number");
      return false;
    }

    if (fdInfo) {
      const now = new Date();
      const maturityDate = new Date(fdInfo.maturityDate);
      const isMatured = now >= maturityDate;
      const isCompleted = fdInfo.status === "completed";

      let maxWithdrawable;

      // If investment is completed, allow withdrawing the full amount
      if (isCompleted) {
        maxWithdrawable = fdInfo.currentValue;
      }
      // For active investments
      else if (fdInfo.status === "active") {
        // If matured, allow full withdrawal
        if (isMatured) {
          maxWithdrawable = fdInfo.currentValue;
        }
        // If not matured, only principal can be withdrawn
        else {
          maxWithdrawable = fdInfo.currentPrincipal;

          // Check if attempting to withdraw full principal before maturity
          if (amt === fdInfo.currentPrincipal) {
            // This will trigger the delete modal later, not an error
            return true;
          }

          // Prevent excessive withdrawal
          if (amt > maxWithdrawable) {
            setErrors(
              `Cannot exceed ₹${maxWithdrawable.toLocaleString("en-IN")}`
            );
            return false;
          }
        }
      }
      // For deleted investments, don't allow withdrawals
      else if (fdInfo.status === "deleted") {
        setErrors("Cannot withdraw from a deleted investment");
        return false;
      }

      if (amt > maxWithdrawable) {
        setErrors(`Cannot exceed ₹${maxWithdrawable.toLocaleString("en-IN")}`);
        return false;
      }
    }

    return true;
  };

  // Update handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const amt = parseFloat(withdrawAmount);

    // Check investment status
    if (fdInfo.status === "deleted") {
      setErrors("Cannot withdraw from a deleted investment");
      return;
    }

    // For active investments
    if (fdInfo.status === "active") {
      const now = new Date();
      const maturityDate = new Date(fdInfo.maturityDate);
      const isMatured = now >= maturityDate;

      // If matured and withdrawing full amount
      if (isMatured && amt === fdInfo.currentValue) {
        setShowCompleteModal(true);
        return;
      }

      // If not matured and trying to withdraw full principal
      if (!isMatured && amt === fdInfo.currentPrincipal) {
        setShowDeleteModal(true);
        return;
      }
    }

    // For completed status, allow normal withdrawal
    if (fdInfo.status === "completed") {
      if (amt === fdInfo.currentValue) {
        setShowCompleteModal(true);
        return;
      }
    }

    // For all other cases, show regular confirmation
    setShowConfirmModal(true);
  };

  const handleConfirmWithdrawal = async () => {
    if (!paymentPlatform) {
      setErrors("Payment platform is required");
      return;
    }
    if (!paymentScreenshot) {
      setErrors("Payment screenshot is required");
      return;
    }

    setSubmitting(true);
    setShowProcessingModal(true);

    try {
      const formData = new FormData();
      formData.append("amount", parseFloat(withdrawAmount));
      formData.append("paymentPlatform", paymentPlatform);
      formData.append("paymentScreenshot", paymentScreenshot);

      await api.post(
        `/admin/clients/${userId}/investments/${fdNumber}/withdraw`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      navigate(`/admin/clients/${userId}/investments/${fdNumber}`);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Withdrawal failed");
    } finally {
      setSubmitting(false);
      setShowProcessingModal(false);
      setShowConfirmModal(false);
    }
  };

  const handleFullWithdrawal = async (action) => {
    if (!paymentPlatform) {
      setErrors("Payment platform is required");
      return;
    }
    if (!paymentScreenshot) {
      setErrors("Payment screenshot is required");
      return;
    }

    setSubmitting(true);
    setShowProcessingModal(true);

    try {
      const formData = new FormData();
      formData.append("amount", parseFloat(withdrawAmount));
      formData.append("paymentPlatform", paymentPlatform);
      formData.append("paymentScreenshot", paymentScreenshot);

      await api.post(
        `/admin/clients/${userId}/investments/${fdNumber}/withdraw`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (action !== "withdraw-only") {
        await api.patch(
          `/admin/clients/${userId}/investments/${fdNumber}/status`,
          {
            status: action === "delete" ? "delete" : "markdone",
          }
        );
        navigate(`/admin/clients/${userId}/investments`);
      } else {
        navigate(`/admin/clients/${userId}/investments/${fdNumber}`);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
      setShowProcessingModal(false);
      setShowCompleteModal(false);
    }
  };

  const handleDeleteWithdrawal = async (deleteInvestment) => {
    if (!paymentPlatform) {
      setErrors("Payment platform is required");
      return;
    }
    if (!paymentScreenshot) {
      setErrors("Payment screenshot is required");
      return;
    }

    setSubmitting(true);
    setShowProcessingModal(true);

    try {
      const formData = new FormData();
      formData.append("amount", parseFloat(withdrawAmount));
      formData.append("paymentPlatform", paymentPlatform);
      formData.append("paymentScreenshot", paymentScreenshot);

      if (deleteInvestment) {
        // Withdraw full principal and delete investment
        await api.post(
          `/admin/clients/${userId}/investments/${fdNumber}/withdraw`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        await api.patch(
          `/admin/clients/${userId}/investments/${fdNumber}/status`,
          {
            status: "delete",
          }
        );
        navigate(`/admin/clients/${userId}/investments`);
      } else {
        // Cancel the operation
        setShowDeleteModal(false);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
      setShowProcessingModal(false);
      setShowDeleteModal(false);
    }
  };

  const handleContactAdmin = (method) => {
    // In a real app, you would implement contact functionality here
    // For now, just close the modal
    setShowContactModal(false);

    if (method === "call") {
      window.location.href = "tel:+919876543210";
    } else if (method === "whatsapp") {
      window.open(
        "https://wa.me/911234567890?text=Hello,%20I%20would%20like%20to%20request%20a%20withdrawal%20for%20my%20investment%20%23" +
          fdNumber,
        "_blank"
      );
    }
  };

  if (!fdInfo) return <div className="text-center py-4">Loading...</div>;

  // Calculate metrics for display
  const startDate = new Date(fdInfo.startDate);
  const maturityDate = new Date(fdInfo.maturityDate);

  const completeModalButtons = [
    {
      text: "Mark Investment as Completed",
      onClick: () => handleFullWithdrawal("complete"),
      color: "success",
      disabled: submitting,
    },
    {
      text: "Delete Investment",
      onClick: () => handleFullWithdrawal("delete"),
      color: "danger",
      disabled: submitting,
    },
    {
      text: "Cancel",
      onClick: () => setShowCompleteModal(false),
      color: "outline",
      disabled: submitting,
    },
  ];

  const confirmModalButtons = [
    {
      text: "Cancel",
      onClick: () => setShowConfirmModal(false),
      color: "outline",
      disabled: submitting,
    },
    {
      text: "Confirm Withdrawal",
      onClick: handleConfirmWithdrawal,
      color: "primary",
      disabled: submitting,
    },
  ];

  const deleteModalButtons = [
    {
      text: "Cancel Withdrawal",
      onClick: () => handleDeleteWithdrawal(false),
      color: "outline",
      disabled: submitting,
    },
    {
      text: "Delete Investment After Withdrawal",
      onClick: () => handleDeleteWithdrawal(true),
      color: "danger",
      disabled: submitting,
    },
  ];

  const contactModalButtons = [
    {
      text: "Call Admin",
      onClick: () => handleContactAdmin("call"),
      color: "primary",
      disabled: submitting,
    },
    {
      text: "WhatsApp Admin",
      onClick: () => handleContactAdmin("whatsapp"),
      color: "success",
      disabled: submitting,
    },
    {
      text: "Cancel",
      onClick: () => setShowContactModal(false),
      color: "outline",
      disabled: submitting,
    },
  ];

  const remainingPrincipal =
    fdInfo.amount -
    (fdInfo.withdrawals?.reduce((sum, w) => sum + w.amount, 0) || 0);

  const totalInterestGains = fdInfo.monthlyBreakdown.reduce(
    (sum, month) => sum + month.interestAmt,
    0
  );

  // Render differently based on admin or user view
  if (isAdminView) {
    // Admin View - Keep the original functionality
    return (
      <>
        <div className="card main-content max-w-xl">
          <h2 className="text-xl font-semibold mb-4">
            Withdraw from FD #{fdNumber}
          </h2>

          {errorMessage && (
            <div className="alert alert-danger mb-4 p-3 bg-red-100 text-red-700 rounded">
              {errorMessage}
            </div>
          )}

          <div className="grid-cards">
            <div className="card mb-0">
              <p className="mb-1">
                <strong>Client:</strong> {userName} ({userId})
              </p>
              <p className="mb-1">
                <strong>Initial Principal Amount:</strong> ₹
                {fdInfo.amount.toLocaleString("en-IN")}
              </p>
              <p className="mb-1">
                <strong>Current Principal Amount:</strong> ₹
                {remainingPrincipal.toLocaleString("en-IN")}
              </p>
              <p className="mb-1">
                <strong>Interest Rate:</strong> {fdInfo.interestRate}%
              </p>
              <p className="mb-1">
                <strong>Current Value:</strong> ₹
                {fdInfo.currentValue.toLocaleString("en-IN")}
              </p>
              <p className="mb-1">
                <strong>Status:</strong> {fdInfo.status}
              </p>
            </div>
            <div className="card mb-0">
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block font-medium mb-2">
                    Withdrawal Amount (₹)
                  </label>
                  <input
                    type="number"
                    name="withdrawAmount"
                    value={withdrawAmount}
                    onChange={handleChange}
                    className="input w-full p-2 border rounded"
                    placeholder="Enter amount to withdraw"
                    required
                  />
                  {errors && <p className="text-red-600 mt-1">{errors}</p>}
                </div>

                <div className="mb-4">
                  <label className="block font-medium mb-2">
                    Payment Platform
                  </label>
                  <select
                    name="paymentPlatform"
                    value={paymentPlatform}
                    onChange={(e) => setPaymentPlatform(e.target.value)}
                    className="input w-full p-2 border rounded"
                    required
                  >
                    <option value="">Select Platform</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="PayTM">PayTM</option>
                    <option value="PhonePe">PhonePe</option>
                    <option value="Google Pay">Google Pay</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block font-medium mb-2">
                    Payment Screenshot
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="input w-full p-2 border rounded"
                    required
                  />
                  {uploadError && (
                    <p className="text-red-600 mt-1">{uploadError}</p>
                  )}
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    className="btn btn-outline px-4 py-2 border rounded"
                    onClick={() =>
                      navigate(
                        `/admin/clients/${userId}/investments/${fdNumber}`
                      )
                    }
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4 py-2 bg-blue-600 text-white rounded"
                    disabled={submitting}
                  >
                    {submitting ? "Processing..." : "Withdraw"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <Modal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Confirm Withdrawal"
          buttons={confirmModalButtons}
          size="md"
        >
          <p>
            Are you sure you want to withdraw ₹
            {parseFloat(withdrawAmount).toLocaleString("en-IN")} from this
            investment?
          </p>
          <p className="mt-2 text-gray-600">
            Payment will be made via: {paymentPlatform}
          </p>
          <p className="mt-2 text-gray-600">
            Current investment value: ₹
            {fdInfo.currentValue.toLocaleString("en-IN")}
          </p>
          <p className="mt-2 text-gray-600">
            Remaining after withdrawal: ₹
            {(
              fdInfo.currentValue - parseFloat(withdrawAmount) || 0
            ).toLocaleString("en-IN")}
          </p>
        </Modal>

        <Modal
          isOpen={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
          title="Full Amount Withdrawal"
          buttons={completeModalButtons}
          size="md"
        >
          <p>
            You're withdrawing the entire investment amount (₹
            {fdInfo?.currentValue?.toLocaleString("en-IN")}). What would you
            like to do with this investment?
          </p>
        </Modal>

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Withdraw Full Principal"
          buttons={deleteModalButtons}
          size="md"
        >
          <p>
            You're attempting to withdraw the full principal amount (₹
            {fdInfo?.currentPrincipal?.toLocaleString("en-IN")}) before
            maturity.
          </p>
          <p className="mt-2 text-red-600">
            Withdrawing the full principal requires deleting this investment.
            Would you like to proceed?
          </p>
        </Modal>

        {/* Processing Modal - Only for admin side */}
        <Modal
          isOpen={showProcessingModal}
          showSpinner={true}
          spinnerText="Processing request"
          spinnerSubText="Please wait while we process your request. You will be redirected shortly."
          size="md"
        />
      </>
    );
  } else {
    // User View - Simplified to just show basic info and contact buttons
    return (
      <>
        <div className="card main-content max-w-xl">
          <h2 className="text-xl font-semibold mb-4">
            Investment Details - FD #{fdNumber}
          </h2>

          {errorMessage && (
            <div className="alert alert-danger mb-4 p-3 bg-red-100 text-red-700 rounded">
              {errorMessage}
            </div>
          )}

          <div className="card mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 text-blue-800">
              Investment Information
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="mb-2">
                  <strong>Principal Amount:</strong> ₹
                  {fdInfo.amount.toLocaleString("en-IN")}
                </p>
                <p className="mb-2">
                  <strong>Current Value:</strong> ₹
                  {fdInfo.currentValue.toLocaleString("en-IN")}
                </p>
                <p className="mb-2">
                  <strong>Interest Rate:</strong> {fdInfo.interestRate}%
                </p>
                <p className="mb-2">
                  <strong>Total Interest Gains:</strong> ₹
                  {totalInterestGains.toLocaleString("en-IN")}
                </p>
                <p className="mb-2">
                  <strong>Total Months Remaining:</strong>{" "}
                  {fdInfo.timeRemaining}
                </p>
              </div>

              <div>
                <p className="mb-2">
                  <strong>Start Date:</strong> {startDate.toLocaleDateString()}
                </p>
                <p className="mb-2">
                  <strong>Maturity Date:</strong>{" "}
                  {maturityDate.toLocaleDateString()}
                </p>
                <p className="mb-2">
                  <strong>Status:</strong>{" "}
                  <span className="font-semibold text-green-600">
                    {fdInfo.status}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 text-yellow-800">
              Withdrawal Instructions
            </h3>

            <p className="mb-4">
              To request a withdrawal from your fixed deposit, please contact
              our admin team directly. Our team will process your request and
              guide you through the withdrawal process.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowContactModal(true)}
                className="btn btn-primary px-4 py-2 bg-blue-600 text-white rounded"
              >
                Contact Admin
              </button>
              <button
                onClick={() => navigate("/investments")}
                className="btn btn-outline px-4 py-2 border rounded"
              >
                Back to Investments
              </button>
            </div>
          </div>
        </div>

        <Modal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          title="Contact Admin for Withdrawal"
          buttons={contactModalButtons}
          size="md"
        >
          <p>
            To request a withdrawal from your fixed deposit #{fdNumber}, please
            contact our admin team through one of the following methods:
          </p>
          <p className="mt-4 font-medium">
            Please have the following information ready:
          </p>
          <ul className="mt-2 list-disc pl-5">
            <li>Your investment ID: #{fdNumber}</li>
            <li>
              Current value: ₹{fdInfo.currentValue.toLocaleString("en-IN")}
            </li>
            <li>Maturity date: {maturityDate.toLocaleDateString()}</li>
            <li>Your withdrawal requirements</li>
          </ul>
        </Modal>
      </>
    );
  }
};

export default WithdrawAmount;
